import type {
	AnswerValue,
	EffectCommand,
	EmittedEvent,
	FlowCtx,
	FlowDef,
	FlowState,
	InfoNode,
	NodeDef,
	QuestionNode,
	TransitionInput,
	TransitionResult,
} from './flowSchema';
import {
	ALLY_IDS_ANSWER_KEY,
	CURP_ANSWER_REGEX,
	EMAIL_ANSWER_REGEX,
	MIN_TEXT_ANSWER_LENGTH,
	REGIMEN_ANSWER_KEY,
} from './flowSchema';
import { normalizeRegimes } from '@/shared/fiscal/canonicalRegime';
import {
	companionOf,
	firstVisibleFrom,
	INFO_SEEN_KEY,
	isChoiceVisible,
	nextVisible,
	outcomeKey,
	previousVisible,
	skipKey,
	staticSkipTarget,
	walk,
} from './walk';

/**
 * EL único escritor del estado del onboarding: reducer puro y atómico.
 * Cliente (fase 0) y heru-ai-service (fase 0b) ejecutan ESTA función — el
 * dueño cambia, la semántica jamás. Toda transición devuelve el estado nuevo
 * COMPLETO más los eventos de analytics que la acompañan: no existe avanzar
 * sin evento, ni completar fuera de un terminal, ni escribir a medias.
 *
 * Las escrituras externas (sync del nodo) tampoco se ejecutan aquí: se
 * DECLARAN en `effects` y el dueño del estado las ejecuta tras la transición.
 */

/** Nombres INTERNOS de los eventos del motor. Se exportan porque el borde (host)
 * los traduce al catálogo de nexus (`onboarding_step_interacted` /
 * `onboarding_completed`): si ese mapa se keyeara con strings sueltos, un rename
 * aquí lo dejaría emitiendo al vacío sin que nada truene. */
export const ENGINE_EVENTS = {
	submitted: 'onb3_node_submitted',
	skipped: 'onb3_node_skipped',
	back: 'onb3_node_back',
	completed: 'onb3_flow_completed',
} as const;

export type EngineEventName = (typeof ENGINE_EVENTS)[keyof typeof ENGINE_EVENTS];

const baseEventPayload = (
	flow: FlowDef,
	nodeId: string,
	node: NodeDef,
): EmittedEvent['payload'] => ({
	flow: flow.flow,
	flow_version: flow.version,
	node: nodeId,
	kind: node.kind,
});

const illegal = (
	state: FlowState,
	error: 'NOT_AT_NODE' | 'ILLEGAL_EDGE' | 'ALREADY_COMPLETED',
): TransitionResult => ({ ok: false, error, state });

/** Materializa la llegada a `landingId` (ya resuelto y visible): si es un
 * terminal, completa — en el MISMO resultado (no hay estado intermedio
 * "avanzó pero no completó"). */
const arriveAt = (
	flow: FlowDef,
	landingId: string | null,
	answers: Record<string, AnswerValue>,
	fallback: FlowState,
	events: EmittedEvent[],
	effects: EffectCommand[] = [],
): TransitionResult => {
	const landingNode = landingId ? flow.nodes[landingId] : undefined;
	if (!landingId || !landingNode) return illegal(fallback, 'ILLEGAL_EDGE');

	if (landingNode.kind === 'terminal') {
		return {
			ok: true,
			state: {
				position: landingId,
				answers,
				completed: true,
				completedVia: landingNode.completedVia,
			},
			events: [
				...events,
				{
					name: ENGINE_EVENTS.completed,
					payload: {
						...baseEventPayload(flow, landingId, landingNode),
						completed_via: landingNode.completedVia,
					},
				},
			],
			effects,
		};
	}

	return {
		ok: true,
		state: { position: landingId, answers, completed: false, completedVia: null },
		events,
		effects,
	};
};

/** Resume: re-camina desde start hasta la frontera (primer visible sin
 * responder). SOLO lo usa 'start' — el avance en vivo es posicional
 * (nextVisible desde donde el usuario está parado); si no lo fuera, responder
 * un nodo revisitado tras un back teletransportaría a la frontera saltándose
 * los nodos intermedios ya respondidos. */
const settleAtFrontier = (
	flow: FlowDef,
	answers: Record<string, AnswerValue>,
	ctx: FlowCtx,
	fallback: FlowState,
): TransitionResult => {
	const { frontier } = walk(flow, answers, ctx);
	return arriveAt(flow, frontier, answers, fallback, []);
};

const toStringList = (value: AnswerValue | undefined): string[] => {
	if (Array.isArray(value)) return value;
	if (typeof value === 'string') return [value];
	return [];
};

const dedupeStable = (values: string[]): string[] => {
	const seen = new Set<string>();
	const out: string[] = [];
	for (const value of values) {
		if (seen.has(value)) continue;
		seen.add(value);
		out.push(value);
	}
	return out;
};

/** Mirror de régimenes: un nodo `declaresRegimes` escribe TAMBIÉN el slot
 * canónico `regimen` con la UNIÓN normalizada de lo ya declarado más lo recién
 * elegido (sentinels fuera, deduplicado). Porta la semántica de buildRegimenMirror
 * (v2) reusando normalizeRegimes — cero duplicación de la tabla de normalización.
 * Si nada normaliza, no toca `regimen` (no lo pisa con vacío). */
const withRegimenMirror = (
	answers: Record<string, AnswerValue>,
	existingRegimen: AnswerValue | undefined,
	incoming: AnswerValue,
): Record<string, AnswerValue> => {
	const merged = normalizeRegimes([
		...toStringList(existingRegimen),
		...toStringList(incoming),
	]);
	if (merged.length === 0) return answers;
	return { ...answers, [REGIMEN_ANSWER_KEY]: merged };
};

/** La UNIÓN acumulativa de `ally_ids`: lo ya declarado más lo nuevo, en orden
 * estable y sin duplicados. A diferencia del régimen NO normaliza — los slugs
 * de aliado son abiertos. Agregar un aliado nunca reemplaza a los anteriores.
 * La consumen el mirror del grid y el prestamp de las campañas de aliado: dos
 * puertas al mismo slot, una sola regla. */
export const mergeAllyIds = (
	existingAllyIds: AnswerValue | undefined,
	incoming: AnswerValue,
): string[] =>
	dedupeStable([...toStringList(existingAllyIds), ...toStringList(incoming)]);

/** Mirror de aliados: un nodo `declaresAlly` escribe TAMBIÉN el slot canónico
 * `ally_ids` (mergeAllyIds). Segundo consumidor del patrón mirror. */
const withAllyMirror = (
	answers: Record<string, AnswerValue>,
	existingAllyIds: AnswerValue | undefined,
	incoming: AnswerValue,
): Record<string, AnswerValue> => {
	const merged = mergeAllyIds(existingAllyIds, incoming);
	if (merged.length === 0) return answers;
	return { ...answers, [ALLY_IDS_ANSWER_KEY]: merged };
};

/** Continue on a `returns` info: back to the question that opened it, with
 * that question's answer (and a skip of it) forgotten, so the user answers it
 * again and the resume lands where the live view is. */
const returnToQuestion = (
	flow: FlowDef,
	state: FlowState,
	nodeId: string,
	node: InfoNode,
	ctx: FlowCtx,
): TransitionResult => {
	const targetId = typeof node.next === 'string' ? node.next : null;
	const target = targetId ? flow.nodes[targetId] : undefined;
	if (!targetId || target?.kind !== 'question') return illegal(state, 'ILLEGAL_EDGE');
	const answers = { ...state.answers };
	delete answers[target.saves];
	delete answers[skipKey(targetId)];
	return arriveAt(flow, firstVisibleFrom(flow, targetId, answers, ctx), answers, state, [
		{ name: ENGINE_EVENTS.submitted, payload: baseEventPayload(flow, nodeId, node) },
	]);
};

const isValidAnswer = (node: NodeDef, value: AnswerValue, ctx: FlowCtx): boolean => {
	if (node.kind !== 'question') return false;
	if (node.variant === 'text') {
		if (typeof value !== 'string') return false;
		if (node.maxLength !== undefined && value.length > node.maxLength) return false;
		if (node.optional && value.trim().length === 0) return true;
		if (node.format === 'email') return EMAIL_ANSWER_REGEX.test(value);
		if (node.format === 'curp') return CURP_ANSWER_REGEX.test(value.trim());
		return value.trim().length >= MIN_TEXT_ANSWER_LENGTH;
	}
	const choices = node.choices ?? {};
	const choiceKeys = Object.keys(choices);
	// A choice hidden by its `visibleWhen` is not a legal answer: the screen
	// never paints it, so accepting it would let a stale or forged input reach
	// a branch the ctx keeps closed.
	const isOffered = (key: string): boolean =>
		choiceKeys.includes(key) && isChoiceVisible(choices[key], ctx);
	if (node.variant === 'multi') {
		return Array.isArray(value) && value.length > 0 && value.every(isOffered);
	}
	return typeof value === 'string' && isOffered(value);
};

/** A companion pick is valid when every value is a choice the screen offers;
 * empty is valid, the companion is optional. Without a companion the input
 * must not carry one. */
const isValidCompanion = (
	companion: QuestionNode | null,
	value: string[] | undefined,
	ctx: FlowCtx,
): boolean => {
	if (!companion) return value === undefined;
	const choices = companion.choices ?? {};
	return (value ?? []).every(
		(key) => key in choices && isChoiceVisible(choices[key], ctx),
	);
};

export const applyTransition = (
	flow: FlowDef,
	state: FlowState,
	input: TransitionInput,
	ctx: FlowCtx,
): TransitionResult => {
	if (input.type === 'start') {
		if (state.completed) return illegal(state, 'ALREADY_COMPLETED');
		return settleAtFrontier(flow, state.answers, ctx, state);
	}

	// Back desde un terminal (estado completado EN SESIÓN): el terminal rico es
	// revisable — volver re-abre el flow en el nodo visible anterior y des-marca
	// completed SOLO en sesión. El record del server jamás des-completa:
	// transitionToPatch nunca re-envía completed y la atribución es write-once;
	// re-completar después tampoco re-atribuye (recordAlreadyCompleted).
	if (state.completed && input.type === 'back' && state.position) {
		const terminalNode = flow.nodes[state.position];
		if (terminalNode?.kind === 'terminal') {
			const previous = previousVisible(flow, state.answers, ctx, state.position);
			if (previous) {
				const answers = { ...state.answers };
				delete answers[skipKey(previous)];
				return {
					ok: true,
					state: {
						...state,
						position: previous,
						answers,
						completed: false,
						completedVia: null,
					},
					events: [
						{
							name: ENGINE_EVENTS.back,
							payload: baseEventPayload(flow, state.position, terminalNode),
						},
					],
					effects: [],
				};
			}
		}
	}

	if (state.completed) return illegal(state, 'ALREADY_COMPLETED');

	const nodeId = state.position;
	const node = nodeId ? flow.nodes[nodeId] : undefined;
	if (!nodeId || !node) return illegal(state, 'NOT_AT_NODE');

	switch (input.type) {
		case 'answer': {
			if (!isValidAnswer(node, input.value, ctx)) return illegal(state, 'ILLEGAL_EDGE');
			const companion = companionOf(flow, node);
			if (!isValidCompanion(companion, input.companion, ctx)) {
				return illegal(state, 'ILLEGAL_EDGE');
			}
			const question = node as Extract<NodeDef, { kind: 'question' }>;
			let answers = { ...state.answers, [question.saves]: input.value };
			if (companion) {
				if (input.companion && input.companion.length > 0) {
					answers[companion.saves] = input.companion;
				} else {
					delete answers[companion.saves];
				}
			}
			if (question.declaresRegimes) {
				answers = withRegimenMirror(
					answers,
					state.answers[REGIMEN_ANSWER_KEY],
					input.value,
				);
			}
			if (question.declaresAlly) {
				answers = withAllyMirror(answers, state.answers[ALLY_IDS_ANSWER_KEY], input.value);
			}
			const effects: EffectCommand[] =
				question.sync && (question.syncWhen === undefined || question.syncWhen === input.value)
				? [{ sync: question.sync, value: input.value }]
				: [];
			return arriveAt(
				flow,
				nextVisible(flow, nodeId, answers, ctx),
				answers,
				state,
				[
					{
						name: ENGINE_EVENTS.submitted,
						payload: {
							...baseEventPayload(flow, nodeId, node),
							answer_key: question.saves,
							answer: Array.isArray(input.value)
								? input.value.join(',')
								: input.value,
						},
					},
				],
				effects,
			);
		}

		case 'outcome': {
			if (node.kind !== 'action' || !node.next[input.value]) {
				return illegal(state, 'ILLEGAL_EDGE');
			}
			const answers = { ...state.answers, [outcomeKey(nodeId)]: input.value };
			if (node.saves && input.data !== undefined) answers[node.saves] = input.data;
			return arriveAt(flow, nextVisible(flow, nodeId, answers, ctx), answers, state, [
				{
					name: ENGINE_EVENTS.submitted,
					payload: {
						...baseEventPayload(flow, nodeId, node),
						action: node.action,
						outcome: input.value,
					},
				},
			]);
		}

		case 'continue': {
			if (node.kind !== 'info') return illegal(state, 'ILLEGAL_EDGE');
			if (input.answer !== undefined) {
				if (!node.returns || !(input.answer in (node.returnAnswers ?? {}))) {
					return illegal(state, 'ILLEGAL_EDGE');
				}
				const returned = returnToQuestion(flow, state, nodeId, node, ctx);
				if (!returned.ok) return returned;
				const answered = applyTransition(
					flow,
					returned.state,
					{ type: 'answer', value: input.answer },
					ctx,
				);
				if (!answered.ok) return illegal(state, 'ILLEGAL_EDGE');
				return {
					...answered,
					events: [...returned.events, ...answered.events],
					effects: [...returned.effects, ...answered.effects],
				};
			}
			if (node.returns) return returnToQuestion(flow, state, nodeId, node, ctx);
			const answers = { ...state.answers, [INFO_SEEN_KEY(nodeId)]: true };
			return arriveAt(flow, nextVisible(flow, nodeId, answers, ctx), answers, state, [
				{
					name: ENGINE_EVENTS.submitted,
					payload: baseEventPayload(flow, nodeId, node),
				},
			]);
		}

		case 'skip': {
			const target = staticSkipTarget(node);
			if (!target || !flow.nodes[target]) return illegal(state, 'ILLEGAL_EDGE');
			// Regla simétrica al back-borra-skip: saltar un nodo BORRA su decisión
			// previa (answer/outcome/seen de una visita anterior vía back). El
			// estado carga UNA decisión por nodo; si quedaran ambas, el replay del
			// walk honraría la vieja y el resume aterrizaría en otro nodo que la
			// vista viva (p.ej. outcome 'submitted' → tarjeta de segundo plano vs
			// skip → continuar sin vincular).
			const answers = { ...state.answers, [skipKey(nodeId)]: target };
			if (node.kind === 'question') {
				delete answers[node.saves];
				const companion = companionOf(flow, node);
				if (companion) delete answers[companion.saves];
			}
			if (node.kind === 'action') {
				delete answers[outcomeKey(nodeId)];
				if (node.saves) delete answers[node.saves];
			}
			if (node.kind === 'info') delete answers[INFO_SEEN_KEY(nodeId)];
			return arriveAt(flow, firstVisibleFrom(flow, target, answers, ctx), answers, state, [
				{ name: ENGINE_EVENTS.skipped, payload: baseEventPayload(flow, nodeId, node) },
			]);
		}

		case 'back': {
			const previous = previousVisible(flow, state.answers, ctx, nodeId);
			if (!previous) return illegal(state, 'ILLEGAL_EDGE');
			// Regresar a un nodo SALTADO es reconsiderarlo: el skip record se
			// borra, si no el resume (walk honra skipKey) aterrizaría DESPUÉS del
			// nodo que la vista viva está mostrando — divergencia resume/vivo.
			const answers = { ...state.answers };
			delete answers[skipKey(previous)];
			return {
				ok: true,
				state: { ...state, position: previous, answers },
				events: [
					{ name: ENGINE_EVENTS.back, payload: baseEventPayload(flow, nodeId, node) },
				],
				effects: [],
			};
		}
	}
};
